import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Alert as AlertIcon } from "@dreamhub/designsystem/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import Text from "@/components/ui/text";
import Alert from "@/pages/deals/components/KanbanBoard/DealCard/Alert";
import { AlertData } from "@/pages/deals/types";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import PopoverHeader from "@/components/ui/popover-header";
import { map } from "lodash";

const TRANSLATION_KEY = "deals.alerts";

interface Props {
  dealId: string;
  alerts: AlertData[];
}

const AlertsIndication: React.FC<Props> = ({ dealId, alerts }) => {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [localAlerts, setLocalAlerts] = useState(alerts);

  useEffect(() => {
    setLocalAlerts(alerts);
  }, [alerts]);

  const onAlertUpdate = (newDealAlerts: AlertData[]) => {
    setLocalAlerts(newDealAlerts);
  };

  const openAlerts = useMemo(
    () => localAlerts?.filter((alert) => !alert.isDismissed),
    [localAlerts]
  );

  const numOfOpenAlerts = (openAlerts || [])?.length;

  return (
    openAlerts &&
    numOfOpenAlerts > 0 && (
      <Popover
        open={isPopoverOpen}
        onOpenChange={(open) => setIsPopoverOpen(open)}
      >
        <PopoverTrigger asChild>
          <Button
            onClick={(e) => {
              e.stopPropagation();
            }}
            tooltipText={`${numOfOpenAlerts} ${t(`${TRANSLATION_KEY}.openAlerts`)}`}
            variant="secondaryNoBg"
            className="border-none p-1"
            selected={isPopoverOpen}
            data-test={`deal-alerts-trigger-${dealId}`}
          >
            <IconContainer
              size="md"
              color="negative"
              className="drop-shadow-red-glow"
            >
              <AlertIcon />
            </IconContainer>

            <Text variant="bodySecondary">{numOfOpenAlerts}</Text>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          padding={0}
          className="w-[25rem]"
          align="start"
          side="right"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          sideOffset={4}
          onClick={(e) => e.stopPropagation()}
          data-test={`deal-alerts-popover-${dealId}`}
        >
          <PopoverHeader
            title={t(`${TRANSLATION_KEY}.popover.title`)}
            badge={numOfOpenAlerts}
            onClose={() => setIsPopoverOpen(false)}
          />
          <div className="p-2 flex flex-col gap-2 max-h-[18rem] overflow-y-auto">
            {map(openAlerts, (alert) => (
              <Alert
                key={alert.id}
                dealId={dealId}
                alert={alert}
                onAlertUpdate={onAlertUpdate}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  );
};

export default AlertsIndication;
